import { html, nothing, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
   enumAttributeConverter,
   Placement,
   PlacementValuesMap,
   Sizes,
   SizesValuesMap,
   Variant,
   VariantValuesMap,
} from '../enum-types.ts';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlBadgeProps, BadgeAs, BadgeTags } from './badge.types.ts';

const BadgeAsValues = {
   [BadgeAs.Normal]: 'normal',
   [BadgeAs.Dot]: 'dot',
   [BadgeAs.Pill]: 'pill',
};

@customElement(BadgeTags.Badge)
export class AtlBadge extends LitElement implements AtlBadgeProps {
   @property({ type: String })
   label?: string;

   @property({ attribute: 'hidden-label', type: String })
   hiddenLabel?: string;

   @property({
      type: String,
      converter: enumAttributeConverter(Variant, Variant.Primary),
   })
   variant?: Variant;

   @property({ type: String, converter: enumAttributeConverter(Sizes) })
   size?: Sizes;

   @property({
      type: String,
      converter: enumAttributeConverter(Placement, Placement.PositionRelative),
   })
   placement?: Placement;

   @property({ type: String, converter: enumAttributeConverter(BadgeAs) })
   as?: BadgeAs;

   @property({ type: Boolean })
   detached: boolean;

   @property({ type: String, attribute: 'wrapper-class' })
   wrapperClass?: string;

   static styles = [...getGlobalStyleSheets()];

   render() {
      const spanClasses: Record<string, boolean> = { 'badge-root': true };
      if (this.wrapperClass) {
         spanClasses[this.wrapperClass] = true;
      }

      const innerSpanClasses: Record<string, boolean> = {
         badge: true,
         [PlacementValuesMap[this.placement || Placement.PositionRelative]]:
            true,
         detached: this.detached === true,
      };

      if (this.variant) {
         innerSpanClasses[VariantValuesMap[this.variant]] = true;
      }

      if (this.size) {
         innerSpanClasses[SizesValuesMap[this.size]] = true;
      }

      if (this.as) {
         innerSpanClasses[BadgeAsValues[this.as]] = true;
      }

      return html`
         <span class="${classMap(spanClasses)}">
            <slot></slot>
            <span class="${classMap(innerSpanClasses)}">
               ${this.as !== BadgeAs.Dot ? html`${this.label}` : nothing}
               ${this.hiddenLabel
                  ? html`<span class="visually-hidden"
                       >${this.hiddenLabel}</span
                    >`
                  : nothing}
            </span>
         </span>
      `;
   }
}
